import { useLocalStorageState } from '@frontend/common';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { getLink } from '../licensed-navbar-item/licensed-navbar-item.controller';
import { LicensedNavbarGroupItemProps } from './licensed-navbar-group-item.component';

interface ViewProps {
    navbarGroupItemRef: React.RefObject<HTMLLIElement | null>;
    navbarItemRef: React.RefObject<HTMLDivElement | null>;

    expanded: boolean;
    isOpen: boolean;

    onClick: () => void;
    onChevronClick: (e: any) => void;
}

const useLicensedNavbarGroupItem = (props: LicensedNavbarGroupItemProps): ViewProps => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const navbarItemRef = useRef<HTMLDivElement | null>(null);
    const navbarGroupItemRef = useRef<HTMLLIElement | null>(null);
    const [navbarPinned] = useLocalStorageState('navbar-pinned', true);
    const [hovered] = useLocalStorageState('navbar-hovered', false);
    const getDefaultFunctionalities = () => {
        return props.functionalities.filter((functionality) => functionality.default);
    };
    const defaultFunctionalities = useMemo(() => getDefaultFunctionalities(), [props.functionalities]);

    const onClick = () => {
        if (defaultFunctionalities.length > 0) {
            const rawLink = getLink(defaultFunctionalities[0]) || `${props.functionalities[0]?.application}/${defaultFunctionalities[0].id}`;
            const link = `${rawLink}?functionalityId=${defaultFunctionalities[0].id}`;
            navigate(link, { state: { functionalityId: defaultFunctionalities[0].id } });
        }
        setIsOpen((val) => !val);
    };
    const onChevronClick = (e: any) => {
        e.stopPropagation();
        if (defaultFunctionalities.length > 0) {
            const rawLink = getLink(defaultFunctionalities[0]) || `${props.functionalities[0]?.application}/${defaultFunctionalities[0].id}`;
            const link = `${rawLink}?functionalityId=${defaultFunctionalities[0].id}`;
            navigate(link, { state: { functionalityId: defaultFunctionalities[0].id } });
        }
        setIsOpen((val) => !val);
    };

    useEffect(() => {
        const applicationPath = pathname.split('/')[1];
        setIsOpen(applicationPath === props.functionalities[0]?.application.toLowerCase());
    }, [pathname]);

    const handleClickOutside = (event: MouseEvent) => {
        if (props.navbarRef.current && props.navbarRef.current.contains(event.target as Node)) {
            if (navbarItemRef.current && navbarItemRef.current.contains(event.target as Node)) return;
            if (navbarGroupItemRef.current && !navbarGroupItemRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
    };

    useEffect(() => {
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    return {
        navbarItemRef,
        navbarGroupItemRef,
        expanded: navbarPinned || hovered,
        isOpen,
        onClick,
        onChevronClick
    };
};

export default useLicensedNavbarGroupItem;
